/**
 * UXCam Conditional Recording
 *
 * The UXCam Web SDK has no pause/resume API. This component works around
 * that by lazily loading the SDK only when the user visits a whitelisted
 * URL path, and calling `abort()` to tear down the session on leave.
 *
 * Key SDK internals exploited:
 * - `window.uxc_initialised` (sic): guard flag checked on script load.
 *   Resetting it to `false` allows re-initialization after an abort.
 * - `abort()`: disconnects the WebSocket, destroys the MutationObserver /
 *   DomCollector, and removes all activity listeners.
 *
 * Each start/stop cycle creates a new UXCam session.
 */
'use client';

import { useEffect, useRef } from 'react';

import { usePathname } from 'next/navigation';

import { config } from '@ps/config';

const LOG_TAG = '[UXCam]';

const UXCAM_SDK_URL = '//websdk-recording.uxcam.com/index.js';
const UXCAM_SCRIPT_ID = 'uxcam-web-sdk';

/**
 * URL path patterns where UXCam recording is enabled.
 *
 * Ported from RN's `uxcamRecording.ts` whitelist with the route names
 * translated to this app's URLs (the same legacy-name → route mapping as
 * `echoIframeTabRoutes.ts`): `/home` → `/dashboard`, `/bio` (the RN bio
 * editor) → `/my-popstore`, `/aiEchoMessagingAssistant` →
 * `/social-engagement-agent`, `/commentchats` → `/comment-to-dm-agent`,
 * `/platform/subscriptions` → `/manage-subscription`. RN's `/promo-pushes`
 * has no surface here and was dropped. The agent surfaces UXCam could never
 * see in RN's WebView era (cross-origin iframe) are native routes now, so
 * every ECHO agent is listed.
 */
const RECORDING_WHITELIST: string[] = [
  '/signup/**',
  '/dashboard/**',
  '/ai-creator-studio/**',
  '/ai-content-generator/**',
  '/pricing-plans/**',
  '/account/**',
  '/my-popstore/**',
  '/social-engagement-agent/**',
  '/comment-to-dm-agent/**',
  '/deal-monitoring-agent/**',
  '/deal-monitoring-agent-setup/**',
  '/auto-selling-agent/**',
  '/growth-rx-agent/**',
  '/follower-ranking-agent/**',
  '/ai-echo-agent-team/**',
  '/manage-subscription/**',
  '/express-setup/**',
];

declare global {
  interface Window {
    uxc?: {
      __t: Array<[string, ...any[]]>;
      __ak: string;
      __o: Record<string, any>;
      event: (name: string, properties?: Record<string, any>) => void;
      setUserIdentity: (identity: string) => void;
      setUserProperty: (key: string, value: any) => void;
      setUserProperties: (properties: Record<string, any>) => void;
      abort?: (notifyServer?: boolean) => void;
    };
    uxc_initialised?: boolean;
  }
}

/**
 * Local dev hosts (localhost / loopback / `.local` / private-LAN IPs) aren't
 * registered origins for the UXCam app, so its `/logs` and websocket calls are
 * CORS-blocked and spam the console. Deployed origins (dev.pop.store, prod) are
 * registered, so recording only makes sense — and only works — there.
 */
function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const { hostname } = window.location;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname === '::1' ||
    hostname.endsWith('.local') ||
    // The workspace dev-runner's Caddy proxy hostnames (local.pop.store et
    // al.) resolve only via a hosts-file entry — just as unregistered with
    // UXCam as bare localhost.
    hostname === 'local.pop.store' ||
    hostname.endsWith('.local.pop.store') ||
    /^10\./.test(hostname) ||
    /^192\.168\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(hostname)
  );
}

function isRecordablePath(pathname: string): boolean {
  return RECORDING_WHITELIST.some(pattern => {
    if (pattern.endsWith('/**')) {
      const prefix = pattern.slice(0, -3);
      return pathname === prefix || pathname.startsWith(prefix + '/');
    }
    return pathname === pattern;
  });
}

function startRecording(apiKey: string): void {
  console.debug(LOG_TAG, 'Starting recording for path:', location.pathname);

  // Reset the SDK singleton guard so it re-initializes on script load.
  window.uxc_initialised = false;

  // Create the stub that queues calls until the SDK is ready.
  window.uxc = {
    __t: [],
    __ak: apiKey,
    __o: {},
    event(n, p) {
      this.__t.push(['event', n, p]);
    },
    setUserIdentity(i) {
      this.__t.push(['setUserIdentity', i]);
    },
    setUserProperty(k, v) {
      this.__t.push(['setUserProperty', k, v]);
    },
    setUserProperties(p) {
      this.__t.push(['setUserProperties', p]);
    },
  };

  // Dynamically load the SDK script.
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = UXCAM_SDK_URL;
  script.async = true;
  script.defer = true;
  script.id = UXCAM_SCRIPT_ID;
  script.crossOrigin = 'anonymous';
  document.head.appendChild(script);
}

function stopRecording(): void {
  console.debug(LOG_TAG, 'Stopping recording');

  // abort() disconnects the websocket, destroys the DOM collector,
  // and removes all activity listeners.
  window.uxc?.abort?.(true);

  // Remove the injected script element.
  document.getElementById(UXCAM_SCRIPT_ID)?.remove();

  // Clear the global.
  window.uxc = undefined;
}

/**
 * Renders nothing. Monitors the current pathname via Next.js router and
 * starts / stops UXCam recording based on {@link RECORDING_WHITELIST}.
 *
 * Include once in the root layout.
 */
export function UXCamRecording() {
  const pathname = usePathname();
  const recordingRef = useRef(false);

  useEffect(() => {
    const apiKey = config.uxcamApiKey;
    if (!apiKey) {
      console.debug(LOG_TAG, 'API key not configured, recording disabled');
      return;
    }

    // Local dev origins are CORS-blocked by UXCam — skip recording there.
    if (isLocalDevHost()) {
      console.debug(
        LOG_TAG,
        'Local dev host, recording disabled:',
        window.location.hostname,
      );
      return;
    }

    const shouldRecord = isRecordablePath(pathname);
    console.debug(
      LOG_TAG,
      'Path changed:',
      pathname,
      shouldRecord ? '(recordable)' : '(skipped)',
    );

    if (shouldRecord && !recordingRef.current) {
      startRecording(apiKey);
      recordingRef.current = true;
    } else if (!shouldRecord && recordingRef.current) {
      stopRecording();
      recordingRef.current = false;
    }
  }, [pathname]);

  return null;
}
